import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSetupStatus } from '../hooks/useSetupStatus'
import { useIsNarrow } from '../hooks/useMediaQuery'
import { colors, font, radius, shadow, space } from '../design/tokens'

interface StepConfig {
  id: 'email' | 'profile'
  done: boolean
  icon: string
  title: string
  desc: string
  cta: string
  /** What the assistant is asked when the button is pressed. */
  ask: string
  /** The form, for anyone who would rather fill one in. */
  to: string
}

/** Where the banner stays quiet: the wizard and the OAuth flow are already the
 * setup, on the chat screen the assistant raises the same gaps itself, and on
 * the lists page it was the top half of the screen — a page about who to
 * approach, opening on two settings forms. It still greets anyone who goes to
 * send (/mail), which is where an unfinished setup actually stops the work. */
const PATHS_TO_SKIP = ['/onboarding', '/oauth', '/settings/email-service', '/chat', '/leads']

const chatRoute = (ask: string) => `/chat?ask=${encodeURIComponent(ask)}`

const SetupGuardBanner: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { emailReady, hasSalesProfile, isLoading } = useSetupStatus()
  const isNarrow = useIsNarrow()

  if (isLoading) return null
  if (PATHS_TO_SKIP.some(p => location.pathname.startsWith(p))) return null
  if (emailReady && hasSalesProfile) return null

  const steps: StepConfig[] = [
    {
      id: 'email',
      done: emailReady,
      icon: '✉️',
      title: 'メール送信を設定',
      desc: 'お好きなプロバイダー（Gmail / Postmark / Resend など）を1つ接続すれば、営業担当の送信元アドレスにも自動で反映されます。',
      cta: 'チャットで設定する',
      ask: 'メール送信の設定をしたい。何をすればいい？',
      to: '/settings/email-service?onboarding=1',
    },
    {
      id: 'profile',
      done: hasSalesProfile,
      icon: '👤',
      title: '営業プロフィールの登録',
      desc: '差出人となる営業担当者を最低1名登録してください。送信用の宛名・署名に使われます。',
      cta: 'チャットで登録する',
      ask: '営業プロフィール（メールの差出人）を登録したい',
      to: '/settings?tab=profiles',
    },
  ]

  const pending = steps.filter(s => !s.done)
  if (pending.length === 0) return null

  // On a phone the full banner is taller than the screen, which would push
  // whatever page it sits on (the chat's input, say) out of reach. Say the
  // same thing in one row and let the step's own screen explain the detail.
  if (isNarrow) {
    const next = pending[0]
    return (
      <div
        role="alert"
        aria-live="polite"
        style={{
          margin: `${space[3]} ${space[4]} 0`,
          padding: `${space[3]}`,
          display: 'flex',
          alignItems: 'center',
          gap: space[3],
          backgroundColor: colors.dangerBg,
          border: `1px solid ${colors.dangerBorder}`,
          borderLeft: `4px solid ${colors.danger}`,
          borderRadius: radius.lg,
        }}
      >
        <span aria-hidden style={{ fontSize: '1.125rem' }}>🚨</span>
        <p
          style={{
            margin: 0,
            flex: 1,
            minWidth: 0,
            fontSize: font.size.sm,
            fontWeight: font.weight.semibold,
            color: '#991B1B',
          }}
        >
          {next.title}が未完了です
          {pending.length > 1 && `（ほか${pending.length - 1}件）`}
        </p>
        <button
          onClick={() => navigate(chatRoute(next.ask))}
          style={{
            flexShrink: 0,
            minHeight: 40,
            padding: `${space[2]} ${space[3]}`,
            fontSize: font.size.xs,
            fontWeight: font.weight.semibold,
            backgroundColor: colors.danger,
            color: 'white',
            border: 'none',
            borderRadius: radius.md,
            cursor: 'pointer',
          }}
        >
          チャットで設定 →
        </button>
      </div>
    )
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        margin: `${space[4]} ${space[6]} 0`,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.dangerBorder}`,
        borderLeft: `4px solid ${colors.danger}`,
        borderRadius: radius.lg,
        boxShadow: shadow.sm,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: `${space[3]} ${space[4]}`,
          backgroundColor: colors.dangerBg,
          borderBottom: `1px solid ${colors.dangerBorder}`,
          display: 'flex',
          gap: space[3],
          alignItems: 'center',
        }}
      >
        <span aria-hidden style={{ fontSize: '1.25rem' }}>🚨</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontWeight: font.weight.bold,
              fontSize: font.size.md,
              color: '#991B1B',
            }}
          >
            サービスを使い始める前に {pending.length} つの設定が必要です
          </p>
          <p
            style={{
              margin: `${space[1]} 0 0`,
              fontSize: font.size.xs,
              color: '#B91C1C',
            }}
          >
            未完了のままだとメール送信・キャンペーン配信は動作しません。
            ボタンを押すと、AI営業チャットが順に聞きながら設定します。
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: pending.length > 1 ? '1fr 1fr' : '1fr',
          gap: space[3],
          padding: space[4],
        }}
      >
        {pending.map(step => (
          <div
            key={step.id}
            style={{
              display: 'flex',
              gap: space[3],
              alignItems: 'flex-start',
              padding: space[3],
              borderRadius: radius.md,
              backgroundColor: '#FFF7F7',
              border: `1px solid ${colors.dangerBorder}`,
            }}
          >
            <div
              aria-hidden
              style={{
                width: 32,
                height: 32,
                borderRadius: radius.full,
                backgroundColor: colors.danger,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                flexShrink: 0,
              }}
            >
              {step.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: font.size.sm,
                  fontWeight: font.weight.semibold,
                  color: colors.textPrimary,
                }}
              >
                {step.title}
              </p>
              <p
                style={{
                  margin: `${space[1]} 0 ${space[2]}`,
                  fontSize: font.size.xs,
                  color: colors.textSecondary,
                  lineHeight: font.lineHeight.normal,
                }}
              >
                {step.desc}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: space[3], flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate(chatRoute(step.ask))}
                  style={{
                    padding: `${space[2]} ${space[3]}`,
                    fontSize: font.size.xs,
                    fontWeight: font.weight.semibold,
                    backgroundColor: colors.danger,
                    color: 'white',
                    border: 'none',
                    borderRadius: radius.md,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = colors.dangerHover
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = colors.danger
                  }}
                >
                  {step.cta} →
                </button>
                <button
                  onClick={() => navigate(step.to)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: font.size.xs,
                    color: colors.textSecondary,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  設定画面で行う
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SetupGuardBanner
